import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import _ from 'underscore';
import { DataServiceContext } from '@bingads-webui-campaign-react/smart-page-context';

const MAKECALL_DEBOUNCE_WAIT_IN_MS = 200;
const MINIMUM_SEARCH_QUERY_LENGTH = 3;

/**
 * Gets a list of campaign category suggestions based on search query and locale.
 * @param {string} searchQuery - Search query.
 * @param {string} locale - locale.
 * @returns {Object[]} - List of suggested categories.
 */
export function useGetCategorySuggestions(searchQuery, locale = 'en-us') {
  const { smartPageService } = useContext(DataServiceContext);
  const [categories, setCategories] = useState(null);

  const fetchCategories = useCallback(payload =>
    smartPageService.getBusinessCategorySuggestions(payload).then((response) => {
      if (response) {
        setCategories(response);
      }
      return response;
    }), [smartPageService]);

  // use debounce to prevent too many requests while user is typing
  const debouncedFetchCategories = useMemo(
    () => _.debounce(fetchCategories, MAKECALL_DEBOUNCE_WAIT_IN_MS),
    [fetchCategories]
  );

  // function for when searchQuery is under the minimum and user presses Enter key
  const getSuggestions = useCallback(() => {
    if (searchQuery.length > 0
      && searchQuery.length < MINIMUM_SEARCH_QUERY_LENGTH) {
      fetchCategories({
        searchQueries: [searchQuery],
        locale,
      });
    }
  }, [fetchCategories, locale, searchQuery]);

  useEffect(() => {
    if (_.size(searchQuery) >= MINIMUM_SEARCH_QUERY_LENGTH) {
      debouncedFetchCategories({
        searchQueries: [searchQuery],
        locale,
      });
    } else {
      setCategories(null);
    }

    return () => debouncedFetchCategories.cancel();
  }, [debouncedFetchCategories, locale, searchQuery]);

  useEffect(() => {
    if (!searchQuery) {
      setCategories(null);
    }
  }, [searchQuery]);

  return { categories, getSuggestions };
}
